import { apiService } from './api'
import { type User, type ProfileResponse, UserSchema } from '../types'
import { type ProfileUpdateData, type PasswordChangeData } from '../validation/schemas'
import { AUTH_ENDPOINTS, USER_ENDPOINTS } from '../../api/endpoints'

class ProfileService {
  // Get current user profile with RBAC data
  async getProfile(): Promise<User> {
    const response = await apiService.get(USER_ENDPOINTS.PROFILE)

    // Handle response format: { code, message, data: { user, roles, permissions, is_super_admin } }
    if (response && typeof response === 'object' && 'data' in response) {
      const data = response as ProfileResponse

      if (data.data?.user) {
        // Merge RBAC data into user object
        const user = {
          ...data.data.user,
          roles: data.data.roles || [],
          permissions: data.data.permissions || [],
          isSuperAdmin: data.data.isSuperAdmin || false,
        }
        return UserSchema.parse(user)
      }
    }

    // Fallback: assume direct user object response
    return UserSchema.parse(response as User)
  }

  // Update current user profile
  async updateProfile(data: ProfileUpdateData): Promise<User> {
    const response = await apiService.put(USER_ENDPOINTS.PROFILE, data)

    // Handle different response formats
    if (response && typeof response === 'object' && 'data' in response) {
      const data = response as { data: { user?: User } }
      if (data.data?.user) {
        return UserSchema.parse(data.data.user)
      }
    }

    // Assume direct user object response
    return UserSchema.parse(response as User)
  }

  // Change user password
  async changePassword(data: PasswordChangeData): Promise<void> {
    await apiService.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
      current_password: data.current_password,
      new_password: data.new_password,
    })
  }
}

export const profileService = new ProfileService()
